// DOM-free helpers for the date picker controller, kept separate so they can be unit tested in node.
// Dates are local Date objects at midnight; times are { hours, minutes }.

const pad = number => String(number).padStart(2, "0")
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

export function makeDate(year, month, day) {
  const date = new Date(year, month - 1, day)
  // Reject overflow such as 31.02., which Date would silently roll into March.
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null
  return date
}

export function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

export function addDays(date, days) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)
}

// Keeps the day of month where possible: 31 Jan + 1 month is 28/29 Feb, not 3 Mar.
export function addMonths(date, months) {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1)
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
  return new Date(target.getFullYear(), target.getMonth(), Math.min(date.getDate(), lastDay))
}

export function sameDay(a, b) {
  return !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

export function compareDays(a, b) {
  return startOfDay(a).getTime() - startOfDay(b).getTime()
}

export function clampDay(date, min, max) {
  if (min && compareDays(date, min) < 0) return startOfDay(min)
  if (max && compareDays(date, max) > 0) return startOfDay(max)
  return date
}

export function outOfRange(date, min, max) {
  return (!!min && compareDays(date, min) < 0) || (!!max && compareDays(date, max) > 0)
}

// ISO values are what the hidden inputs submit, matching native date, datetime-local and time inputs.
export function toISODate(date) {
  return date ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` : ""
}

export function toISOTime(time) {
  return time ? `${pad(time.hours)}:${pad(time.minutes)}` : ""
}

export function parseISODate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? "").trim())
  return match ? makeDate(+match[1], +match[2], +match[3]) : null
}

export function parseISOTime(value) {
  const match = /(?:^|T|\s)(\d{2}):(\d{2})/.exec(String(value ?? "").trim())
  return match ? makeTime(+match[1], +match[2]) : null
}

function makeTime(hours, minutes) {
  if (hours > 23 || minutes > 59 || hours < 0 || minutes < 0) return null
  return { hours, minutes }
}

// strftime subset used by the app's I18n date formats (%d.%m.%Y, %-d %b %y).
export function formatDate(date, pattern, monthNames = MONTH_NAMES) {
  if (!date) return ""
  return pattern
    .replace(/%-d/g, date.getDate())
    .replace(/%d/g, pad(date.getDate()))
    .replace(/%-m/g, date.getMonth() + 1)
    .replace(/%m/g, pad(date.getMonth() + 1))
    .replace(/%b/g, monthNames[date.getMonth()])
    .replace(/%Y/g, date.getFullYear())
    .replace(/%y/g, pad(date.getFullYear() % 100))
}

// Order of day, month and year in a pattern, used to read typed numbers. Falls back to day-month-year.
export function fieldOrder(pattern) {
  const order = [...pattern.matchAll(/%-?([dmYy])/g)].map(match => match[1].toLowerCase())
  return order.length === 3 && new Set(order).size === 3 ? order : ["d", "m", "y"]
}

const KEYWORDS = {
  today: 0, heute: 0, t: 0, h: 0,
  tomorrow: 1, morgen: 1,
  yesterday: -1, gestern: -1
}

function expandYear(year, today) {
  if (year >= 100) return year
  // Two-digit years land within 50 years of today, so "26" is 2026 and "98" is 1998.
  const century = Math.floor(today.getFullYear() / 100) * 100
  const candidate = century + year
  return candidate > today.getFullYear() + 50 ? candidate - 100 : candidate
}

function monthIndex(word, monthNames) {
  const matches = names => names.findIndex(name => word.startsWith(name.toLocaleLowerCase().replace(/\.$/, "")))
  const index = matches(monthNames)
  return index === -1 ? matches(MONTH_NAMES) : index
}

// Reads what people actually type: "30.10.2026", "30.10.26", "30.10.", "3010", "30102026", "7 Aug 26",
// "7. August", "2026-10-30", "today"/"heute", "+3" or "-7" (days from today).
export function parseDateInput(text, { pattern = "%d.%m.%Y", today = new Date(), monthNames = MONTH_NAMES } = {}) {
  const input = String(text ?? "").trim().toLocaleLowerCase()
  if (!input) return null
  const base = startOfDay(today)

  if (input in KEYWORDS) return addDays(base, KEYWORDS[input])

  const relative = /^([+-])\s*(\d{1,4})$/.exec(input)
  if (relative) return addDays(base, (relative[1] === "-" ? -1 : 1) * Number(relative[2]))

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(input)
  if (iso) return makeDate(+iso[1], +iso[2], +iso[3])

  const named = /^(\d{1,2})[.\s]*(\p{L}+)[.\s]*(\d{2}|\d{4})?$/u.exec(input)
  if (named) {
    const month = monthIndex(named[2], monthNames)
    if (month === -1) return null
    return makeDate(named[3] ? expandYear(Number(named[3]), base) : base.getFullYear(), month + 1, Number(named[1]))
  }

  const order = fieldOrder(pattern)
  let parts = input.split(/[^\d]+/).filter(Boolean)

  // Compact digits: "3010" (day and month), "301026" or "30102026".
  if (parts.length === 1 && /^\d{4}$|^\d{6}$|^\d{8}$/.test(parts[0])) {
    const digits = parts[0]
    const yearLength = digits.length === 8 ? 4 : 2
    parts = []
    let rest = digits
    for (const field of order) {
      if (!rest) break
      const length = field === "y" ? yearLength : 2
      parts.push(rest.slice(0, length))
      rest = rest.slice(length)
    }
  }

  if (parts.length < 2 || parts.length > 3) return null

  const values = {}
  order.filter(field => parts.length === 3 || field !== "y").forEach((field, index) => { values[field] = Number(parts[index]) })
  const year = values.y === undefined ? base.getFullYear() : expandYear(values.y, base)
  return makeDate(year, values.m, values.d)
}

// "9" -> 09:00, "930" -> 09:30, "9:30", "9.30", "14h", "2pm", "2:30 pm", "now"/"jetzt".
export function parseTimeInput(text, { now = new Date() } = {}) {
  const input = String(text ?? "").trim().toLocaleLowerCase().replace(/\s+/g, "")
  if (!input) return null
  if (input === "now" || input === "jetzt") return { hours: now.getHours(), minutes: now.getMinutes() }

  const match = /^(\d{1,2})(?:[:.h]?(\d{2}))?h?(am|pm)?$/.exec(input)
  if (!match) return null

  let hours = Number(match[1])
  const minutes = Number(match[2] || 0)
  if (match[3]) {
    if (hours < 1 || hours > 12) return null
    hours = (hours % 12) + (match[3] === "pm" ? 12 : 0)
  }
  return makeTime(hours, minutes)
}

// "30.10.2026 14:30", "30.10.2026, 14:30" or just a date (time falls back to defaultTime).
export function parseDateTimeInput(text, { pattern, today = new Date(), defaultTime = { hours: 0, minutes: 0 } } = {}) {
  const input = String(text ?? "").trim()
  if (!input) return null

  const iso = /^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2})/.exec(input)
  if (iso) {
    const date = parseISODate(iso[1])
    const time = parseISOTime(iso[2])
    return date && time ? { date, time } : null
  }

  // A trailing token is a time when it looks like one ("14:30", "2pm", "9h"); otherwise the whole
  // input is tried as a date first, so "30. 10. 2026" is not read as 30.10. at 20:26.
  const [, datePart, timePart] = /^(.*?)(?:[,\s]+(\S+))?$/.exec(input)
  const split = () => {
    if (!timePart) return null
    const date = parseDateInput(datePart, { pattern, today })
    const time = parseTimeInput(timePart, { now: today })
    return date && time ? { date, time } : null
  }
  const whole = () => {
    const date = parseDateInput(input, { pattern, today })
    return date ? { date, time: defaultTime } : null
  }

  return /[:h]|am$|pm$/i.test(timePart || "") ? split() || whole() : whole() || split()
}

export const RANGE_SEPARATOR = " → "
const RANGE_SPLIT = /\s*(?:→|–|—|\bto\b|\bbis\b)\s*|\s+-\s+/i

export function formatRange(start, end, pattern, monthNames = MONTH_NAMES) {
  if (!start && !end) return ""
  return [formatDate(start, pattern, monthNames), formatDate(end, pattern, monthNames)].join(RANGE_SEPARATOR).trim()
}

// Reads "1.10.2026 - 30.10.2026", "1.10. bis 30.10.", "1.10.2026 →" (open end) or "→ 30.10.2026" (open start).
// A single date is a one-day range. Reversed ranges are swapped.
export function parseRangeInput(text, options = {}) {
  const input = String(text ?? "").trim()
  if (!input) return null

  const separator = RANGE_SPLIT.exec(input)
  if (!separator) {
    const date = parseDateInput(input, options)
    return date ? { start: date, end: date } : null
  }

  const left = input.slice(0, separator.index).trim()
  const right = input.slice(separator.index + separator[0].length).trim()
  const start = left ? parseDateInput(left, options) : null
  const end = right ? parseDateInput(right, options) : null
  if ((left && !start) || (right && !end) || (!start && !end)) return null
  return start && end && compareDays(start, end) > 0 ? { start: end, end: start } : { start, end }
}

// 42 days (6 weeks) covering the month, so the grid never changes height while paging.
export function calendarDays(year, month, weekStart = 1) {
  const first = new Date(year, month, 1)
  const offset = (first.getDay() - weekStart + 7) % 7
  const start = addDays(first, -offset)
  return Array.from({ length: 42 }, (_, index) => {
    const date = addDays(start, index)
    return { date, inMonth: date.getMonth() === month }
  })
}

export function timeSlots(step = 15) {
  const slots = []
  for (let minutes = 0; minutes < 24 * 60; minutes += step) {
    slots.push({ hours: Math.floor(minutes / 60), minutes: minutes % 60 })
  }
  return slots
}

export const RANGE_PRESETS = ["today", "yesterday", "last_7_days", "last_30_days", "last_90_days", "this_month", "last_month", "this_year"]

export function presetRange(key, today = new Date()) {
  const base = startOfDay(today)
  const year = base.getFullYear()
  const month = base.getMonth()
  switch (key) {
    case "today": return { start: base, end: base }
    case "yesterday": return { start: addDays(base, -1), end: addDays(base, -1) }
    case "last_7_days": return { start: addDays(base, -6), end: base }
    case "last_30_days": return { start: addDays(base, -29), end: base }
    case "last_90_days": return { start: addDays(base, -89), end: base }
    case "this_month": return { start: new Date(year, month, 1), end: new Date(year, month + 1, 0) }
    case "last_month": return { start: new Date(year, month - 1, 1), end: new Date(year, month, 0) }
    case "this_year": return { start: new Date(year, 0, 1), end: new Date(year, 11, 31) }
    default: return null
  }
}

// A preset covers only the part of its period inside the bounds, and is not offered when none of it is.
export function boundedPreset(key, today, min, max) {
  const range = presetRange(key, today)
  if (!range || (max && compareDays(range.start, max) > 0) || (min && compareDays(range.end, min) < 0)) return null
  return { start: clampDay(range.start, min, max), end: clampDay(range.end, min, max) }
}
